import React, { useState } from 'react';
import { Upload, CheckCircle, XCircle, AlertCircle, Loader, Image as ImageIcon } from 'lucide-react';
import { verifySlip, recordSlipPayment } from '../services/slipOkService';
import { auth } from '../lib/firebase';

interface SlipUploadPaymentProps {
  amount: number;
  planName: string;
  planTier: string;
  receiverAccount?: string;
  onPaymentSuccess: (paymentId: string) => void;
  onPaymentError: (error: string) => void;
  additionalData?: any;
}

const SlipUploadPayment: React.FC<SlipUploadPaymentProps> = ({
  amount,
  planName,
  planTier,
  receiverAccount,
  onPaymentSuccess,
  onPaymentError,
  additionalData
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationResult, setVerificationResult] = useState<any>(null);
  const [error, setError] = useState<string>('');

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('กรุณาเลือกไฟล์รูปภาพเท่านั้น');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('ไฟล์มีขนาดใหญ่เกินไป (สูงสุด 5MB)');
      return;
    }

    setSelectedFile(file);
    setError('');
    setVerificationResult(null);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleVerifySlip = async () => {
    if (!selectedFile) {
      setError('กรุณาเลือกรูปสลิปก่อน');
      return;
    }

    const user = auth.currentUser;
    // Allow guest checkout - user can be null for new purchases

    setIsVerifying(true);
    setError('');
    setVerificationResult(null);

    try {
      console.log('[SlipUpload] Verifying slip...', { amount, receiverAccount });

      // Verify slip using SlipOK API
      const result = await verifySlip(selectedFile, amount, receiverAccount);

      console.log('[SlipUpload] Verification result:', result);

      if (!result.success) {
        setError(result.error || 'ไม่สามารถตรวจสอบสลิปได้');
        setVerificationResult({ success: false, error: result.error });
        onPaymentError(result.error || 'Slip verification failed');
        return;
      }

      // Slip is valid - record payment
      setVerificationResult(result);

      console.log('[SlipUpload] Recording payment...');
      const paymentId = await recordSlipPayment(
        user?.uid || null, // Allow null for guest checkout
        planTier,
        amount,
        result.data,
        additionalData
      );

      console.log('[SlipUpload] Payment recorded:', paymentId);
      onPaymentSuccess(paymentId);

    } catch (err: any) {
      console.error('[SlipUpload] Error:', err);
      const errorMessage = err.message || 'เกิดข้อผิดพลาดในการตรวจสอบสลิป';
      setError(errorMessage);
      setVerificationResult({ success: false, error: errorMessage });
      onPaymentError(errorMessage);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setError('');
    setVerificationResult(null);
  };

  return (
    <div className="space-y-6">
      {/* Instructions */}
      <div className="bg-[#EAF6F5] border-2 border-[#BEE3DE] rounded-xl p-4">
        <h3 className="font-bold text-blue-900 mb-2 flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          วิธีการชำระเงิน
        </h3>
        <ol className="text-sm text-[#0E7C86] space-y-1 ml-6 list-decimal">
          <li>สแกน QR Code ด้านบนด้วยแอพธนาคาร</li>
          <li>โอนเงินจำนวน <strong>฿{amount.toLocaleString()}</strong></li>
          <li>ถ่ายภาพหน้าจอสลิปการโอนเงิน</li>
          <li>อัพโหลดสลิปด้านล่าง</li>
          <li>รอระบบตรวจสอบอัตโนมัติ (ประมาณ 5-10 วินาที)</li>
        </ol>
      </div>

      {/* File Upload Area */}
      <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-[#00B4A1] transition-colors">
        {!previewUrl ? (
          <label className="cursor-pointer block">
            <input
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
              disabled={isVerifying}
            />
            <div className="flex flex-col items-center gap-3">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                <Upload className="w-8 h-8 text-[#00B4A1]" />
              </div>
              <div>
                <p className="text-lg font-semibold text-gray-900">อัพโหลดสลิปการโอนเงิน</p>
                <p className="text-sm text-gray-600 mt-1">คลิกเพื่อเลือกไฟล์ หรือลากไฟล์มาวาง</p>
                <p className="text-xs text-gray-500 mt-1">รองรับ JPG, PNG (สูงสุด 5MB)</p>
              </div>
            </div>
          </label>
        ) : (
          <div className="space-y-4">
            {/* Preview */}
            <div className="relative inline-block">
              <img
                src={previewUrl}
                alt="Slip preview"
                className="max-h-64 rounded-lg border-2 border-gray-200"
              />
              {!isVerifying && !verificationResult && (
                <button
                  onClick={handleRemoveFile}
                  className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-2 hover:bg-red-600 transition-colors"
                >
                  <XCircle className="w-5 h-5" />
                </button>
              )}
            </div>

            {/* File Info */}
            <div className="text-sm text-gray-600">
              <p className="font-semibold">{selectedFile?.name}</p>
              <p>{(selectedFile!.size / 1024).toFixed(2)} KB</p>
            </div>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4">
          <div className={`flex items-start gap-3 ${selectedFile ? 'mb-4' : ''}`}>
            <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold text-red-900">เกิดข้อผิดพลาด</p>
              <p className="text-sm text-red-800 mt-1">{error}</p>
            </div>
          </div>
          {selectedFile && (
            <button
              onClick={handleRemoveFile}
              className="w-full py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl font-semibold transition-colors flex items-center justify-center gap-2"
            >
              <Upload className="w-5 h-5" />
              อัพโหลดสลิปใหม่
            </button>
          )}
        </div>
      )}

      {/* Verification Result */}
      {verificationResult && verificationResult.success && (
        <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4">
          <div className="flex items-start gap-3 mb-3">
            <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-bold text-green-900 text-lg">ตรวจสอบสลิปสำเร็จ!</p>
              <p className="text-sm text-green-800 mt-1">ระบบกำลังดำเนินการอนุมัติการชำระเงิน...</p>
            </div>
          </div>

          {/* Slip Details */}
          <div className="bg-white rounded-lg p-4 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">จำนวนเงิน:</span>
              <span className="font-semibold text-gray-900">฿{verificationResult.data.amount.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">ผู้โอน:</span>
              <span className="font-semibold text-gray-900">{verificationResult.data.sender.name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">ธนาคาร:</span>
              <span className="font-semibold text-gray-900">{verificationResult.data.sender.bank}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">เลขที่ธุรกรรม:</span>
              <span className="font-semibold text-gray-900">{verificationResult.data.transactionId}</span>
            </div>
          </div>
        </div>
      )}

      {/* Verify Button */}
      {selectedFile && !verificationResult && (
        <button
          onClick={handleVerifySlip}
          disabled={isVerifying}
          className={`w-full py-4 rounded-xl font-bold text-lg transition-all ${
            isVerifying
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-gradient-to-r from-[#00B4A1] to-[#3A93B9] text-white hover:shadow-xl hover:scale-105'
          }`}
        >
          {isVerifying ? (
            <span className="flex items-center justify-center gap-2">
              <Loader className="w-5 h-5 animate-spin" />
              กำลังตรวจสอบสลิป...
            </span>
          ) : (
            'ตรวจสอบสลิปและชำระเงิน'
          )}
        </button>
      )}

      {/* Info */}
      <div className="text-xs text-gray-500 text-center space-y-1">
        <p>✓ ระบบตรวจสอบสลิปอัตโนมัติด้วย AI</p>
        <p>✓ ปลอดภัย รวดเร็ว ไม่เกิน 10 วินาที</p>
        <p>✓ ตรวจจับสลิปปลอมได้ 99%+</p>
      </div>
    </div>
  );
};

export default SlipUploadPayment;
